// Utility to compute in-app route for a given FCM payload
// Supports notification types: VIBE_JOIN, VIBE_CANCELLED, VIBE_CREATED, VIBE_REMINDER

/**
 * Safely extract a string value from various possible keys
 */
const get = (obj, keys, fallback = undefined) => {
  for (const k of keys) {
    if (obj && Object.prototype.hasOwnProperty.call(obj, k) && obj[k] != null) {
      return obj[k];
    }
  }
  return fallback;
};

/**
 * Normalize and return the notification type (e.g., VIBE_CREATED)
 * @param {any} payload FCM foreground/background payload
 * @returns {string|undefined}
 */
export const getNotificationType = (payload) => {
  const data = payload?.data || {};
  const typeRaw = get(data, ["type", "notification_type", "Type"], undefined);
  return typeof typeRaw === "string" ? typeRaw.trim().toUpperCase() : undefined;
};

/**
 * Extract a vibe id from payload if available
 * @param {any} payload
 * @returns {string|undefined}
 */
export const getVibeIdFromPayload = (payload) => {
  const data = payload?.data || {};
  // Prefer explicit id-like fields
  let id = get(
    data,
    ["id", "vibe_id", "vibeId", "VibeId", "VIBE_ID", "event_id", "eventId"],
    undefined
  );

  // Some payloads send the id in a generic "data" field (e.g., "2733")
  if (id == null) {
    const maybeId = get(data, ["data"], undefined);
    if (
      maybeId != null &&
      ((typeof maybeId === "string" && /^\d+$/.test(maybeId)) ||
        typeof maybeId === "number")
    ) {
      id = maybeId;
    }
  }

  if (id == null) return undefined;
  // Ensure it is a string for URL building
  return String(id);
};

/**
 * Compute the in-app route for a notification
 * Priority: explicit URL in payload > type-based routing > notifications page
 * @param {any} payload
 * @returns {string} route path
 */
export const getRouteForNotification = (payload) => {
  const data = payload?.data || {};
  // 1) If backend provided a full/relative URL, honor it
  const explicitUrl = get(
    data,
    ["url", "URL", "deep_link", "deeplink"],
    undefined
  );
  if (explicitUrl && typeof explicitUrl === "string") {
    return explicitUrl;
  }

  // 2) Build from known types and ids
  const type = getNotificationType(payload);
  const vibeId = getVibeIdFromPayload(payload);

  switch (type) {
    case "VIBE_JOIN":
      // User should review/accept/reject on the vibe detail screen
      return vibeId ? `/vibe/${vibeId}` : "/notifications";
    case "VIBE_CANCELLED":
      // Show the vibe detail (it will reflect cancelled status)
      return vibeId ? `/vibe/${vibeId}` : "/my-vibes";
    case "VIBE_CREATED":
      // Newly created vibe detail is the most relevant
      return vibeId ? `/vibe/${vibeId}` : "/my-vibes";
    case "VIBE_REMINDER":
      // Reminder from cron – try the vibe detail, else calendar
      return vibeId ? `/vibe/${vibeId}` : "/calendar";
    default:
      return "/notifications";
  }
};

/**
 * Navigate to the computed route (simple hard redirect)
 * @param {string} route
 */
export const navigateToRoute = (route) => {
  try {
    if (!route) return;
    // Prefer client-side navigation if available
    if (typeof window !== "undefined") {
      // If the route is absolute http(s), just assign
      if (/^https?:\/\//i.test(route)) {
        window.location.href = route;
      } else {
        // Ensure leading slash for in-app routes
        const path = route.startsWith("/") ? route : `/${route}`;
        window.location.href = path;
      }
    }
  } catch (e) {
    // Silent failover
    // eslint-disable-next-line no-console
  }
};
